import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Button
} from "@mui/material";

import Header from "./components/Header";
import CompanyCard from "./components/CompanyCard";
import SearchBar from "./components/SearchBar";
import Filters from "./components/Filters";
import SortBar from "./components/SortBar";
import ResetFilters from "./components/ResetFilters";
import Overview from "./components/Overview";
import TabMiniCharts from "./components/TabMiniCharts";
import Login from "./components/Login";

import exportFeedbackToCSV from "./helpers/exportFeedbackToCSV.js";

function App() {
  // -------------------------------
  // AUTH STATE
  // -------------------------------
  const [authenticated, setAuthenticated] = useState(
    localStorage.getItem("lead_auth") === "true"
  );

  // -------------------------------
  // MAIN STATE
  // -------------------------------
  const [companies, setCompanies] = useState([]);
  const [feedbackData, setFeedbackData] = useState({});
  const [loadedFeedback, setLoadedFeedback] = useState(false);

  const [currentTab, setCurrentTab] = useState("OVERVIEW");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [sortBy, setSortBy] = useState("");

  const [visibleCount, setVisibleCount] = useState(20);

  // -------------------------------------------------------
  // LOAD FEEDBACK FIRST
  // -------------------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem("lead_feedback_structured");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          setFeedbackData(parsed);
        }
      } catch (err) {
        console.error("Error parsing saved feedback:", err);
      }
    }
    setLoadedFeedback(true);
  }, []);

  // -------------------------------------------------------
  // SAVE FEEDBACK
  // -------------------------------------------------------
  useEffect(() => {
    if (loadedFeedback) {
      localStorage.setItem(
        "lead_feedback_structured",
        JSON.stringify(feedbackData)
      );
    }
  }, [feedbackData, loadedFeedback]);

  // -------------------------------------------------------
  // SHARED BUTTON STYLE
  // -------------------------------------------------------
  const actionButtonStyle = {
    fontWeight: 600,
    borderWidth: 2,
    textTransform: "none",
    px: 2.5,
    py: 1,
    height: 40,
    "&:hover": { borderWidth: 2 }
  };

  // -------------------------------------------------------
  // INDUSTRY BUCKET MAP
  // -------------------------------------------------------
  const BUCKET_MAP = {
    biotechnology: "Biotech",
    "life science": "Biotech",
    oncology: "Biotech",
    therapeutics: "Biotech",

    pharmaceutical: "Pharmaceutical",

    "health care": "Healthcare",
    medical: "Healthcare",

    "artificial intelligence": "Artificial Intelligence",
    "artificial intelligence (ai)": "Artificial Intelligence",

    agtech: "AgTech",
    agriculture: "AgTech",
    "animal feed": "AgTech",
    "animal health": "AgTech"
  };

  const normalizeBuckets = (industries) => {
    if (!industries) return [];
    const list = Array.isArray(industries)
      ? industries
      : typeof industries === "string"
      ? industries.split(",")
      : [];

    const cleaned = list
      .filter((x) => typeof x === "string")
      .map((x) => x.trim().toLowerCase());

    const buckets = new Set();
    cleaned.forEach((x) => {
      if (BUCKET_MAP[x]) buckets.add(BUCKET_MAP[x]);
    });

    return Array.from(buckets);
  };

  const normalizeCountry = (location) => {
    if (!location || typeof location !== "string") return "Unknown";
    const parts = location.split(",").map((p) => p.trim());
    return parts[parts.length - 1] || "Unknown";
  };

  const mapFundingStage = (type) => {
    if (!type) return "Unknown";
    const t = type.toLowerCase();

    if (t.includes("seed")) return "Seed";
    if (t.includes("series a")) return "Series A";
    if (t.includes("series b")) return "Series B";
    if (t.includes("series c")) return "Series C";
    if (t.includes("grant")) return "Grant";

    return type || "Unknown";
  };

  // -------------------------------------------------------
  // LOAD JSON AFTER FEEDBACK
  // -------------------------------------------------------
  useEffect(() => {
    if (!loadedFeedback) return;

    fetch("/data/data_qwen.json")
      .then((res) => res.json())
      .then((raw) => {
        const normalized = raw.map((c) => {
          const companyId = c.organizationNameUrl;

          const rankString = c.cbRankCompany || c.cbRankOrganization || "";
          const rankNumber =
            typeof rankString === "string"
              ? parseInt(rankString.replace(/,/g, ""), 10)
              : null;

          return {
            id: companyId,
            "Organization Name": c.organizationName || "Unknown",
            Description: c.description || c.fullDescription || "Unknown",

            Industries: c.industries || [],
            BucketIndustries: normalizeBuckets(c.industries),

            Website: c.website || "",
            Crunchbase: c.organizationNameUrl || "",

            Founded: c.foundedDate || null,
            "Last Funding Date": c.lastFundingDate || null,

            "Headquarters Location": normalizeCountry(c.headquartersLocation),
            "Crunchbase Rank": rankNumber,
            Employees: c.numberOfEmployees || "Unknown",

            "Lead Type":
              c["Scope classification"] === "INTEREST"
                ? "ACCEPT"
                : c["Scope classification"] === "NOT_INTEREST"
                ? "NOT ACCEPT"
                : "FUZZY",

            Reasoning: c.Justification || "No justification provided",
            "Investment Stage": mapFundingStage(c.lastFundingType),

            original: c
          };
        });

        setCompanies(normalized);
      })
      .catch((err) => console.error("JSON Load Error:", err));
  }, [loadedFeedback]);

  // -------------------------------------------------------
  // LOGIN GATE
  // -------------------------------------------------------
  if (!authenticated) {
    return <Login onLogin={() => setAuthenticated(true)} />;
  }

  // -------------------------------------------------------
  // RESET FILTERS
  // -------------------------------------------------------
  const resetAll = () => {
    setSearchTerm("");
    setSelectedIndustry("");
    setSelectedCountry("");
    setSelectedStage("");
    setSortBy("");
    setVisibleCount(20);
  };

  // -------------------------------------------------------
  // FILTERING & SORTING
  // -------------------------------------------------------
  const filtered = companies
    .filter((c) =>
      currentTab === "OVERVIEW" ? true : c["Lead Type"] === currentTab
    )
    .filter((c) =>
      c["Organization Name"]?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((c) =>
      selectedIndustry ? c.BucketIndustries.includes(selectedIndustry) : true
    )
    .filter((c) =>
      selectedCountry ? c["Headquarters Location"] === selectedCountry : true
    )
    .filter((c) =>
      selectedStage ? c["Investment Stage"] === selectedStage : true
    );

  const sorted = filtered.sort((a, b) => {
    if (sortBy === "name-asc")
      return a["Organization Name"].localeCompare(b["Organization Name"]);
    if (sortBy === "name-desc")
      return b["Organization Name"].localeCompare(a["Organization Name"]);

    if (sortBy === "rank-asc")
      return (a["Crunchbase Rank"] || 999999) -
             (b["Crunchbase Rank"] || 999999);

    if (sortBy === "rank-desc")
      return (b["Crunchbase Rank"] || 0) -
             (a["Crunchbase Rank"] || 0);

    const parseEmployees = (value) => {
      if (!value) return 0;

      if (typeof value === "number") return value;

      if (value.includes("-")) {
        const [min, max] = value.split("-").map((n) => parseInt(n.trim(), 10));
        return (min + max) / 2;
      }

      return parseInt(value, 10) || 0;
    };

    if (sortBy === "employees-desc")
      return parseEmployees(b.Employees) - parseEmployees(a.Employees);

    if (sortBy === "employees-asc")
      return parseEmployees(a.Employees) - parseEmployees(b.Employees);

    return 0;
  });

  const visibleCompanies = sorted.slice(0, visibleCount);

  // -------------------------------------------------------
  // LOADING GATE
  // -------------------------------------------------------
  if (!loadedFeedback) return <div>Loading...</div>;

  // -------------------------------------------------------
  // RENDER UI
  // -------------------------------------------------------
  return (
    <>
      <Header />

      <Container maxWidth="md" sx={{ paddingTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          Brokancode – Pharmaron: Lead Discovery PoC
        </Typography>

        {/* ACTION BAR - ALL BUTTONS ON THE RIGHT */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mb: 2
          }}
        >
          <Button
            variant="outlined"
            color="success"
            sx={actionButtonStyle}
            onClick={() => exportFeedbackToCSV(sorted, feedbackData)}   // <-- UPDATED HERE
          >
            EXPORT FEEDBACK CSV
          </Button>

          <ResetFilters resetAll={resetAll} sx={actionButtonStyle} />

          <Button
            variant="outlined"
            color="error"
            sx={actionButtonStyle}
            onClick={() => {
              localStorage.removeItem("lead_auth");
              window.location.reload();
            }}
          >
            LOGOUT
          </Button>
        </Box>

        <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

        <Filters
          companies={companies}
          selectedIndustry={selectedIndustry}
          setSelectedIndustry={setSelectedIndustry}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          selectedStage={selectedStage}
          setSelectedStage={setSelectedStage}
        />

        <SortBar sortBy={sortBy} setSortBy={setSortBy} />

        <Tabs
          value={currentTab}
          onChange={(e, v) => {
            setCurrentTab(v);
            setVisibleCount(20);
          }}
          textColor="primary"
          indicatorColor="primary"
          sx={{ marginBottom: 3 }}
        >
          <Tab label="Overview" value="OVERVIEW" />
          <Tab label="Leads" value="ACCEPT" />
          <Tab label="Discard" value="NOT ACCEPT" />
          <Tab label="Unsure" value="FUZZY" />
        </Tabs>

        {currentTab === "OVERVIEW" ? (
          <Overview companies={companies} />
        ) : (
          <Box>
            <TabMiniCharts companies={sorted} />

            {visibleCompanies.map((c) => (
              <CompanyCard
                key={c.id}
                company={c}
                feedbackData={feedbackData}
                setFeedbackData={setFeedbackData}
              />
            ))}

            {visibleCount < sorted.length && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setVisibleCount(visibleCount + 20)}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </>
  );
}

export default App;
